import React, { createContext, useContext, useEffect, useState } from 'react';

import { getAllGlobalStats } from 'utils/stats';
import { ChainId, GlobalStats } from 'utils/types';
import { useChain } from './chain';

type State = Record<ChainId, Partial<GlobalStats>>;

type ContextProps = State & {
  currentStats: Partial<GlobalStats>;
  globalSupply: number | undefined;
  globalBurnt: number | undefined;
};

const InitialState: State = {
  [ChainId.EthMainnet]: {},
  [ChainId.BscMainnet]: {},
  [ChainId.XDai]: {},
  [ChainId.MaticMainnet]: {},
};

const StatsContext = createContext({} as ContextProps);

export const useStats = () => useContext(StatsContext);

export const StatsProvider: React.FC = (props) => {
  const [stats, setStats] = useState(InitialState);
  const { id: chainId } = useChain();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const nextStats = await getAllGlobalStats();

        // Normalize ETH total supply
        const {
          [ChainId.EthMainnet]: { totalSupply: totalSupplyEth },
          [ChainId.BscMainnet]: { totalSupply: totalSupplyBsc },
          [ChainId.XDai]: { totalSupply: totalSupplyXDai },
          [ChainId.MaticMainnet]: { totalSupply: totalSupplyMatic },
        } = nextStats;
        if (totalSupplyEth && totalSupplyBsc && totalSupplyXDai && totalSupplyMatic) {
          nextStats[ChainId.EthMainnet].totalSupply =
            totalSupplyEth - totalSupplyBsc - totalSupplyXDai - totalSupplyMatic;
        }

        setStats(nextStats);
      } catch (err) {
        console.error('Error fetching global stats', err);
      }
    };

    fetchStats();
    if (process.env.NODE_ENV !== 'development') {
      const fetchInterval = setInterval(fetchStats, 30000);
      return () => clearInterval(fetchInterval);
    }
  }, []);

  const {
    [ChainId.EthMainnet]: { totalSupply: totalSupplyEth, totalBurnt: globalBurnt },
    [ChainId.BscMainnet]: { totalSupply: totalSupplyBsc },
    [ChainId.XDai]: { totalSupply: totalSupplyXDai },
    [ChainId.MaticMainnet]: { totalSupply: totalSupplyMatic },
  } = stats;

  let globalSupply: number | undefined;
  if (totalSupplyEth && totalSupplyBsc && totalSupplyXDai && totalSupplyMatic) {
    globalSupply = totalSupplyEth + totalSupplyBsc + totalSupplyXDai + totalSupplyMatic;
  }

  return (
    <StatsContext.Provider
      {...props}
      value={{
        ...stats,
        currentStats: chainId ? stats[chainId] : {},
        globalSupply,
        globalBurnt,
      }}
    />
  );
};
